import { useLiveSearch } from '@/scopes/search/search/domain/live-search/hooks';
import { Category } from './Category';
import { isEmpty } from 'ramda';

import { BaseInput, ButtonLoaderIcon, tk, Typography } from '@sunwebgroup/ui-components';

import { useLiveSearchStore } from '@/scopes/search/search/infrastructure/live-search/store';
import { styled } from 'styled-components';
import type { ChangeEventHandler } from 'react';
import * as React from 'react';
import { useDebouncedCallback } from '@/scopes/common/components/hooks/useDebouncedCallback';
import { useIntl } from '@/intl';
import { HighlightedText } from '@/scopes/search/components/PartiallyHighlightedText';

export const Loader = () => {
  const { isFetching, isLoading } = useLiveSearch();
  const { query } = useLiveSearchStore();
  if (isFetching || isLoading) {
    return (
      <>
        <HighlightedText $variant="body-md">{query}</HighlightedText>
        {' ... '}
        <LoaderIcon />
      </>
    );
  }
  return null;
};

const LoaderIcon = styled(ButtonLoaderIcon)`
  color: ${tk('color_semantic_bg_primary')};
`;

export const Body = () => {
  const { data, isFetching, isLoading } = useLiveSearch();
  if (!isFetching && !isLoading && !isEmpty(data) && data) {
    return <Results />;
  }
  return null;
};

export const NoResults = () => {
  const intl = useIntl();
  const { data, isFetching, isLoading } = useLiveSearch();
  const { query } = useLiveSearchStore();

  if (!isFetching && !isLoading && isEmpty(data) && query) {
    return (
      <>
        <Typography $variant="body-md">{intl.formatMessage({ id: 'live-search_no_results' })}</Typography>{' '}
        <HighlightedText $variant="body-md">{query}</HighlightedText>
      </>
    );
  }
  return null;
};

const Results = () => {
  const intl = useIntl();
  const { data } = useLiveSearch();

  return (
    <>
      {data?.flightTickets && data.flightTickets.length > 0 && (
        <Category
          title={intl.formatMessage({ id: 'live-search_category_flighttickets' })}
          icon="IconAirplane"
          items={data.flightTickets}
        />
      )}
      {data?.destinations && data?.destinations.length > 0 && (
        <Category
          title={intl.formatMessage({ id: 'live-search_category_destinations' })}
          icon="IconPin"
          items={data.destinations}
        />
      )}
      {data?.accommodations && data.accommodations.length > 0 && (
        <Category
          title={intl.formatMessage({ id: 'live-search_category_accommodations' })}
          icon="IconHome"
          items={data.accommodations}
        />
      )}
      {data?.information && data.information.length > 0 && (
        <Category
          title={intl.formatMessage({ id: 'live-search_category_information' })}
          icon="IconInformationCircle"
          items={data.information}
        />
      )}
    </>
  );
};

export const Header = () => {
  const intl = useIntl();
  const { query, setQuery } = useLiveSearchStore();
  const debouncedSetQuery = useDebouncedCallback(setQuery, 500);
  const [inputValue, setInputValue] = React.useState(query || '');

  const onChange: ChangeEventHandler = (e) => {
    const { value } = e.target as HTMLInputElement;
    setInputValue(value);
    debouncedSetQuery(value);
  };
  return (
    <Input>
      <BaseInput.Box
        as="input"
        onChange={onChange}
        type="text"
        placeholder={intl.formatMessage({ id: 'live-search_search_button_text' })}
        value={inputValue}
      />
    </Input>
  );
};

const Input = styled(BaseInput.Root)`
  margin: ${tk('spacing_md')};
  width: 100%;
  display: block;

  @media ${tk('mq_tablet')} {
    width: auto;
  }
`;
